import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FilterSelectItemComponent } from '@epicuro-next/ataraxia/filter-select';
import { IconsModule } from '@epicuro-next/ataraxia/icons';
import { InputDateRangeModule } from '@epicuro-next/ataraxia/input-date-range';
import { UTCDateRange } from '@epicuro-next/ataraxia/models';
import { TooltipDirective } from '@epicuro-next/ataraxia/tooltip';
import { orderFilterLabels } from '@epicuro-next/constants/features/localization';
import { ActiveFilter, Filter } from '@epicuro-next/models/filters';
import { OrderFilterId } from '@epicuro-next/models/order-book';
import {
  getISODate,
  getUTCNow,
  isEqual,
  utcDateTime,
} from '@epicuro-next/utilities';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  tap,
} from 'rxjs';
import { filter } from 'rxjs/operators';

import { selectInitialFilterCount } from '../../config/config.selectors';
import { isPlacedDateFilter } from '../../shared';
import { OrdersFiltersStore } from '../../store/order-filters-store/order-filters.store';

@Component({
  selector: 'epc-order-filters-container',
  imports: [
    CommonModule,
    FilterSelectItemComponent,
    IconsModule,
    InputDateRangeModule,
    ReactiveFormsModule,
    TooltipDirective,
  ],
  templateUrl: './order-filters-container.component.html',
  styleUrls: ['./order-filters-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderFiltersContainerComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private filtersStore = inject(OrdersFiltersStore);

  private store = inject(Store);

  protected allFiltersShown = false;

  protected orderFilters$ = this.filtersStore.orderFilters$;

  protected activeFilters$ = this.filtersStore.activeFilters$;
  protected isResetFiltersAvailable$ =
    this.filtersStore.isResetFiltersAvailable$;

  protected initialFilterCount$ = this.store.select(selectInitialFilterCount);

  public rangeControl: FormControl<Date[] | undefined> = new FormControl();
  public periodValue$ = new BehaviorSubject<UTCDateRange | null>(null);

  public readonly showMoreFilterAvailable$ = combineLatest([
    this.initialFilterCount$,
    this.orderFilters$,
  ]).pipe(
    map(
      ([initialShowedFiltersCount, allFilters]) =>
        (initialShowedFiltersCount ?? 0) < allFilters.length,
    ),
  );

  public readonly dateRangePlaceholder =
    orderFilterLabels[OrderFilterId.PlacedDate];

  private readonly placedDateFilter$ = this.filtersStore.placedDateFilter$.pipe(
    filter(Boolean),
    distinctUntilChanged(isEqual),
    takeUntilDestroyed(this.destroyRef),
  );

  public readonly minDate$ = this.placedDateFilter$.pipe(
    map((placedFilter) =>
      utcDateTime(placedFilter.values?.[0]?.name ?? '1920-01-01').toISODate(),
    ),
  );

  public readonly maxDate$ = this.placedDateFilter$.pipe(
    map((placedFilter) =>
      placedFilter?.values?.length && placedFilter.values.length > 1
        ? utcDateTime(
            placedFilter.values[placedFilter.values.length - 1].name,
          ).toISODate()
        : getUTCNow(),
    ),
  );

  public ngOnInit() {
    this.rangeControl.valueChanges
      .pipe(
        tap((range) => {
          const values = (range ?? []).map(getISODate);
          this.filtersStore.updateActiveFilters(
            OrderFilterId.PlacedDate,
            values,
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.rangeControlSetterSubscribe();
  }

  public getActiveValues(
    filterItem: Filter,
    selectedValues: ActiveFilter[] | null,
  ) {
    return selectedValues?.find(({ id }) => filterItem.id === id)?.values ?? [];
  }

  public onToggleFilter(filterItem: Filter, values: string[] | null) {
    this.filtersStore.updateActiveFilters(filterItem.id, values ?? []);
  }

  public resetFilters() {
    this.filtersStore.resetFilters();
  }

  public trackByValue(_: number, item: Filter) {
    return item.id;
  }

  private rangeControlSetterSubscribe() {
    this.activeFilters$
      .pipe(
        filter(Boolean),
        map((activeFilters: ActiveFilter[]) =>
          activeFilters.find(isPlacedDateFilter),
        ),
        distinctUntilChanged(isEqual),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((placedDateRangeFilter: ActiveFilter | undefined) => {
        let period: UTCDateRange | null = null;
        let range: Date[] = [];
        if (placedDateRangeFilter?.values?.length) {
          period = {
            from: placedDateRangeFilter.values[0],
            to: placedDateRangeFilter.values[1],
          };

          range = Object.values<string>(period).map((item) => new Date(item));
        }

        this.periodValue$.next(period);
        this.rangeControl.setValue(range, {
          emitEvent: false,
        });
      });
  }
}
